import { z } from 'zod';
import { RecordIdSchema } from './identifiers';
import { InstantSchema } from './time';

/**
 * Import and export (CORE-IMPORT-001, 002, 003).
 *
 * **Import is preview, then commit — all or nothing.** An uploaded file is parsed and every row
 * validated before anything is written; the preview lists each row's errors by row and column; and a
 * commit either writes every row in one transaction or refuses. There is no "import the good rows" —
 * a partial import that silently drops rows is the failure CORE-IMPORT-002 exists to prevent.
 *
 * **Export is a controlled read** (CORE-IMPORT-003): the owning module's scoped, field-restricted
 * query, bounded in size, written as formula-safe CSV, recorded in the audit trail, and handed over as
 * a short-lived link rather than a permanent file.
 */

/** What can be imported. Each kind is an importer registered at the composition root. */
export const IMPORT_KINDS = [
  'referenceItems',
  'leads',
  'bankStatementLines',
  // Manual campaign tracking (ADR-0042): dated performance figures from a platform's export.
  'campaignPerformance',
  // Attendance from a time clock or spreadsheet (HR-TIME-001): all rows or none.
  'attendance',
  // Client onboarding (Release 1): mapped columns, per-record commit, durable source keys.
  'customers',
  'units',
  'contracts',
  'installments',
  'receipts',
] as const;
export const ImportKindSchema = z.enum(IMPORT_KINDS);
export type ImportKind = z.infer<typeof ImportKindSchema>;

/**
 * The leads file's columns, in order (CRM-LEAD-006). Every imported lead is owned by the person who
 * imports it; handing leads out is a separate, audited assignment.
 */
export const LEAD_IMPORT_COLUMNS = [
  { name: 'name', required: true },
  { name: 'primary_phone', required: true },
  { name: 'secondary_phone', required: false },
  { name: 'email', required: false },
  { name: 'source', required: true },
  { name: 'branch_code', required: true },
  { name: 'notes', required: false },
  /** A marketing campaign's exact name or id, in the same legal entity (ADR-0042). Optional. */
  { name: 'campaign', required: false },
] as const;

/**
 * A bank statement's lines (FIN-BANK-003): appended to an open statement named by the treasury account's
 * code and the statement's reference. Exactly one of `debit` (money out) and `credit` (money in) is
 * filled on each row. No bank is connected: the file is what the bank gave the client.
 */
export const BANK_STATEMENT_IMPORT_COLUMNS = [
  { name: 'account_code', required: true },
  { name: 'statement_reference', required: true },
  { name: 'value_date', required: true },
  { name: 'description', required: true },
  { name: 'reference', required: false },
  { name: 'debit', required: false },
  { name: 'credit', required: false },
] as const;

/**
 * An attendance file's columns (HR-TIME-001): one row per employee and day. Times are `HH:MM` on the
 * organization's clock, as the time clock printed them; at least one of the two is filled.
 */
export const ATTENDANCE_IMPORT_COLUMNS = [
  /** The employee's number as on their record (e.g. EMP-2026-00012). */
  { name: 'employee_number', required: true },
  { name: 'date', required: true },
  { name: 'check_in', required: false },
  { name: 'check_out', required: false },
  { name: 'note', required: false },
] as const;

/** The reference-items file's columns, in order. Header names match case-insensitively. */
export const REFERENCE_IMPORT_COLUMNS = [
  { name: 'list', required: true },
  { name: 'code', required: true },
  { name: 'label_ar', required: true },
  { name: 'label_en', required: true },
  { name: 'description_ar', required: false },
  { name: 'description_en', required: false },
  { name: 'sort_order', required: false },
] as const;

export const IMPORT_FORMATS = ['csv', 'xlsx'] as const;
export const IMPORT_STATES = [
  /** Read and its header matched to the columns; waiting for a person to confirm the mapping. */
  'uploaded',
  /** Parsed and validated; nothing written. */
  'previewed',
  /** Being written, one record at a time, under a lease; an interrupted run is resumed. */
  'committing',
  /** Every row written (atomic kinds: in one transaction; onboarding kinds: imported or already there). */
  'committed',
  /** Written as far as it could be: some records were refused while writing and are listed as failed. */
  'completedWithFailures',
  /** Withdrawn by a person before commit. */
  'discarded',
] as const;
export const ImportStateSchema = z.enum(IMPORT_STATES);

/** An upload may hold at most this many data rows; a larger file is refused, never truncated. */
export const IMPORT_MAX_ROWS = 5000;
/** At most this many bytes. */
export const IMPORT_MAX_BYTES = 5 * 1024 * 1024;
/** How long a preview may wait for its commit. */
export const IMPORT_PREVIEW_TTL_HOURS = 24;

export const IMPORT_ISSUE_SEVERITIES = ['error', 'warning'] as const;

export const ImportIssueSchema = z.strictObject({
  /** The spreadsheet row, counting the header as row 1 — what the person sees in their file. */
  row: z.number().int().positive(),
  column: z.string().optional(),
  code: z.string(),
  /** Absent means `error`. A warning never blocks a commit; it asks a person to look. */
  severity: z.enum(IMPORT_ISSUE_SEVERITIES).optional(),
});
export type ImportIssue = z.infer<typeof ImportIssueSchema>;

export const ImportBatchSchema = z.strictObject({
  batchId: RecordIdSchema,
  kind: ImportKindSchema,
  format: z.enum(IMPORT_FORMATS),
  fileName: z.string(),
  state: ImportStateSchema,
  totalRows: z.number().int().nonnegative(),
  validRows: z.number().int().nonnegative(),
  invalidRows: z.number().int().nonnegative(),
  /** Up to the first 200 issues; `issueCount` says how many there are in all. */
  issues: z.array(ImportIssueSchema),
  issueCount: z.number().int().nonnegative(),
  /** The first rows as they will be written, for a person to check before committing. */
  preview: z.array(z.record(z.string(), z.string())),
  createdBy: z.string(),
  createdAt: InstantSchema,
  expiresAt: InstantSchema,
  committedAt: InstantSchema.optional(),
  version: z.number().int().positive(),
  /* -------- onboarding kinds only (absent on reference items, leads and bank lines) -------- */
  /** `atomic`: one transaction for the file. `perRecord`: one transaction per record, resumable. */
  mode: z.enum(['atomic', 'perRecord']).optional(),
  /** Where the file's identifiers come from, e.g. `LEGACY-ERP`. Source keys are unique within it. */
  sourceNamespace: z.string().optional(),
  /** The file's header cells, in order — what the mapping chooses from. */
  headers: z.array(z.string()).optional(),
  /** Column → the header it reads (`null` = not supplied). The server's suggestion until confirmed. */
  mapping: z.record(z.string(), z.string().nullable()).optional(),
  /** Counts at validation: rows with an error, rows with only warnings, rows already imported. */
  errorCount: z.number().int().nonnegative().optional(),
  warningCount: z.number().int().nonnegative().optional(),
  duplicateRows: z.number().int().nonnegative().optional(),
  /** Progress of a per-record commit, persisted with each record. */
  progress: z
    .strictObject({
      units: z.number().int().nonnegative(),
      processed: z.number().int().nonnegative(),
      imported: z.number().int().nonnegative(),
      alreadyImported: z.number().int().nonnegative(),
      staged: z.number().int().nonnegative(),
      failed: z.number().int().nonnegative(),
      skipped: z.number().int().nonnegative(),
    })
    .optional(),
  /** True while committing and the previous run's lease has lapsed: the person may resume. */
  canResume: z.boolean().optional(),
  /** A stable code for why the last run stopped, when it stopped early. */
  lastError: z.string().optional(),
  finishedAt: InstantSchema.optional(),
  /** Set once the retention period removed the stored file and rows; counts and results remain. */
  purgedAt: InstantSchema.optional(),
});
export type ImportBatch = z.infer<typeof ImportBatchSchema>;

/** A source namespace: where identifiers come from. Upper-case code, like a business code. */
export const SourceNamespaceSchema = z
  .string()
  .trim()
  .min(2)
  .max(40)
  .regex(/^[A-Z0-9][A-Z0-9_-]*$/, { message: 'SOURCE_NAMESPACE_EXPECTED' });

export const UploadImportQuerySchema = z.strictObject({
  kind: ImportKindSchema,
  fileName: z.string().trim().min(1).max(200),
  /** Required for the onboarding kinds. */
  namespace: SourceNamespaceSchema.optional(),
});

/** Confirm the column mapping and validate (onboarding kinds). Nothing is written to the business. */
export const ValidateImportSchema = z.strictObject({
  mapping: z.record(z.string().max(80), z.string().max(200).nullable()),
  expectedVersion: z.number().int().positive(),
});
export type ValidateImport = z.infer<typeof ValidateImportSchema>;

export const ImportBatchQuerySchema = z.strictObject({
  kind: ImportKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
});
export type ImportBatchQuery = z.infer<typeof ImportBatchQuerySchema>;

export const ImportBatchPageSchema = z.strictObject({
  items: z.array(ImportBatchSchema),
  nextCursor: z.string().optional(),
});
export type ImportBatchPage = z.infer<typeof ImportBatchPageSchema>;

/** What happened to a record (one row, or a group of rows such as one contract's schedule). */
export const IMPORT_ROW_STATUSES = [
  'imported',
  /** Its source identifier was already imported — by an earlier run of this batch or by another. */
  'alreadyImported',
  /** Validated and kept, waiting for a later file (a contract waits for its schedule). */
  'staged',
  /** Refused while writing; nothing of it was written. */
  'failed',
  /** Not attempted because a record it depends on is missing or failed. */
  'skipped',
] as const;
export const ImportRowStatusSchema = z.enum(IMPORT_ROW_STATUSES);
export type ImportRowStatus = z.infer<typeof ImportRowStatusSchema>;

export const ImportRowResultSchema = z.strictObject({
  /** The first file row of the record. */
  row: z.number().int().positive(),
  /** Every file row the record was made from (a schedule is several rows). */
  rows: z.array(z.number().int().positive()),
  sourceId: z.string(),
  status: ImportRowStatusSchema,
  code: z.string().optional(),
  /** The record's business number or code, when it has one. */
  reference: z.string().optional(),
  /** The record, for a link; never displayed. */
  recordId: z.string().optional(),
});
export type ImportRowResult = z.infer<typeof ImportRowResultSchema>;

export const ImportRowResultPageSchema = z.strictObject({
  items: z.array(ImportRowResultSchema),
  nextCursor: z.number().int().optional(),
});
export type ImportRowResultPage = z.infer<typeof ImportRowResultPageSchema>;

export const ImportRowResultQuerySchema = z.strictObject({
  status: ImportRowStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  /** The last `row` of the previous page. */
  cursor: z.coerce.number().int().nonnegative().optional(),
});
export type ImportRowResultQuery = z.infer<typeof ImportRowResultQuerySchema>;

/**
 * Days an import's source file and stored rows are kept after the batch ends (committed, discarded
 * or expired). Afterwards the file is removed from private storage and the rows are cleared; the
 * batch's counts, issues and row results — the evidence — stay. Business records are never touched.
 */
export const IMPORT_RETENTION_DAYS = 30;

/** A per-record commit holds its batch for this long between progress writes. */
export const IMPORT_LEASE_SECONDS = 120;

export const CommitImportSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
});

/* ---------------------------------------------------------------- exports */

export const EXPORT_KINDS = [
  'leads',
  'units',
  'campaigns',
  // Finance reports (FIN-REPORT-008): the same figures as the screens, as the reader may see them.
  'trialBalance',
  'receivablesAgeing',
  'payablesAgeing',
  'budgetUsage',
  'projectCost',
  'assetRegister',
  'stockBalances',
] as const;
export const ExportKindSchema = z.enum(EXPORT_KINDS);
export type ExportKind = z.infer<typeof ExportKindSchema>;

/** The most rows one export may hold. A larger result is refused with a request to narrow it. */
export const EXPORT_MAX_ROWS = 5000;

export const CreateExportSchema = z.strictObject({
  kind: ExportKindSchema,
  /** A report's parameters (entity, period, budget…), validated by the report's own exporter. */
  params: z.record(z.string().max(40), z.string().max(200)).optional(),
});

export const ExportResultSchema = z.strictObject({
  exportId: RecordIdSchema,
  kind: ExportKindSchema,
  rowCount: z.number().int().nonnegative(),
  /** The columns written — restricted fields the caller may not see are absent, not blank. */
  columns: z.array(z.string()),
  url: z.string(),
  expiresAt: InstantSchema,
  generatedAt: InstantSchema.optional(),
});
export type ExportResult = z.infer<typeof ExportResultSchema>;

export const IMPORT_AUDIT_ACTIONS = {
  uploaded: 'import.uploaded',
  previewed: 'import.previewed',
  committed: 'import.committed',
  commitStarted: 'import.commitStarted',
  commitFinished: 'import.commitFinished',
  commitRefused: 'import.commitRefused',
  discarded: 'import.discarded',
  purged: 'import.purged',
  exported: 'export.created',
} as const;
